import { useState, useCallback, useEffect } from 'react'
import { Box, Typography, Grid, Fab, Button, Tooltip, Alert } from '@mui/material'
import { ForkRight, Add, Compare } from '@mui/icons-material'
import { usePlantContext } from '../hooks/usePlantContext'
import { api } from '../services/api'
import LoadingState from '../components/common/LoadingState'
import PlanSelector from '../components/scenarios/PlanSelector'
import ScenarioCard from '../components/scenarios/ScenarioCard'
import CreateScenarioDialog from '../components/scenarios/CreateScenarioDialog'
import CompareView from '../components/scenarios/CompareView'
import ApprovalDialog from '../components/scenarios/ApprovalDialog'

const daysAgo = (d) => new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString()

// -- Fallback demo data used when API returns empty/null --
const DEMO_PLAN = {
  id: 'plan-001',
  name: 'Budget 2026 — Base Plan',
  status: 'APPROVED',
  createdBy: 'H. Allmann',
  createdAt: daysAgo(30),
  approvedAt: daysAgo(9),
}

const DEMO_SCENARIOS = [
  {
    id: 'sc-001',
    name: 'GMI Soups — Full Volume',
    description: 'Models impact of taking on full GMI soup volume across all lines starting May 2026.',
    status: 'ACTIVE',
    createdBy: 'M. Simon',
    createdAt: daysAgo(3),
    parentId: 'plan-001',
    metrics: {
      totalCases: 1_845_200,
      totalECs: 1_264_800,
      belowDOS: 6,
      capacityOverloads: 3,
    },
    compareMetrics: {
      capacityUtilL1: 92.4,
      capacityUtilL2: 88.1,
      capacityUtilL3: 76.5,
      belowMinDOS: 6,
      weightedAvgDOS: 48.2,
      totalCases: 1_845_200,
      totalECs: 1_264_800,
      budgetVariance: 4.2,
    },
  },
  {
    id: 'sc-002',
    name: 'GMI Soups — Phase 1 Only',
    description: 'Partial GMI volume: soup SKUs only for Line 1 and 2, starting July 2026.',
    status: 'ACTIVE',
    createdBy: 'M. Simon',
    createdAt: daysAgo(2),
    parentId: 'plan-001',
    metrics: {
      totalCases: 1_720_400,
      totalECs: 1_198_300,
      belowDOS: 3,
      capacityOverloads: 1,
    },
    compareMetrics: {
      capacityUtilL1: 85.6,
      capacityUtilL2: 82.3,
      capacityUtilL3: 71.0,
      belowMinDOS: 3,
      weightedAvgDOS: 55.8,
      totalCases: 1_720_400,
      totalECs: 1_198_300,
      budgetVariance: 1.8,
    },
  },
  {
    id: 'sc-003',
    name: 'Chili SKU Rationalization',
    description: 'Discontinue 4 low-velocity chili SKUs to free capacity for new products.',
    status: 'ACTIVE',
    createdBy: 'H. Allmann',
    createdAt: daysAgo(5),
    parentId: 'plan-001',
    metrics: {
      totalCases: 1_680_100,
      totalECs: 1_152_600,
      belowDOS: 2,
      capacityOverloads: 0,
    },
    compareMetrics: {
      capacityUtilL1: 78.2,
      capacityUtilL2: 74.9,
      capacityUtilL3: 68.3,
      belowMinDOS: 2,
      weightedAvgDOS: 62.4,
      totalCases: 1_680_100,
      totalECs: 1_152_600,
      budgetVariance: -2.1,
    },
  },
]

export default function Scenarios() {
  const { plant, planContext, setPlanContext, scenarioContext, setScenarioContext } = usePlantContext()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [plans, setPlans] = useState([])
  const [scenarios, setScenarios] = useState([])
  const [activePlan, setActivePlan] = useState(null)
  const [activeScenario, setActiveScenario] = useState(null)
  const [pinnedIds, setPinnedIds] = useState(new Set())

  const [createOpen, setCreateOpen] = useState(false)
  const [approvalOpen, setApprovalOpen] = useState(false)
  const [approvalTarget, setApprovalTarget] = useState(null)

  const [compareSelection, setCompareSelection] = useState([])
  const [comparing, setComparing] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function fetchPlans() {
      setLoading(true)
      setError(null)
      try {
        const data = await api.get(`/plans?plant=${plant.plantCode}`)
        if (cancelled) return
        const planList = data?.length ? data : [DEMO_PLAN] // fallback to demo
        setPlans(planList)
        const approved = planList.find((p) => p.status === 'Approved') || planList[0]
        setActivePlan(approved)
        setPlanContext(approved)
      } catch (err) {
        if (cancelled) return
        setError(err.message)
        // fallback to demo data on error
        setPlans([DEMO_PLAN])
        setActivePlan(DEMO_PLAN)
        setPlanContext(DEMO_PLAN)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchPlans()
    return () => { cancelled = true }
  }, [plant.plantCode, setPlanContext])

  useEffect(() => {
    if (!activePlan) return
    let cancelled = false
    async function fetchScenarios() {
      setLoading(true)
      setError(null)
      try {
        const data = await api.get(`/scenarios?planVersionId=${activePlan.id}`)
        if (cancelled) return
        setScenarios(data?.length ? data : DEMO_SCENARIOS) // fallback to demo
      } catch (err) {
        if (cancelled) return
        setError(err.message)
        setScenarios(DEMO_SCENARIOS) // fallback to demo data on error
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchScenarios()
    return () => { cancelled = true }
  }, [activePlan])

  const handleSelectPlan = useCallback((plan) => {
    setActivePlan(plan)
    setActiveScenario(null)
    setPlanContext(plan)
    setScenarioContext(null)
  }, [setPlanContext, setScenarioContext])

  const handleSelectScenario = useCallback((sc) => {
    setActiveScenario(sc)
    setScenarioContext(sc)
  }, [setScenarioContext])

  const handleToggleCompare = useCallback((sc) => {
    setCompareSelection((prev) => {
      const exists = prev.find((s) => s.id === sc.id)
      if (exists) return prev.filter((s) => s.id !== sc.id)
      if (prev.length >= 2) return [prev[1], sc]
      return [...prev, sc]
    })
  }, [])

  const handleStartCompare = useCallback(() => {
    if (compareSelection.length === 2) setComparing(true)
  }, [compareSelection])

  const handleCloseCompare = useCallback(() => {
    setComparing(false)
    setCompareSelection([])
  }, [])

  const handlePin = useCallback((sc) => {
    setPinnedIds((prev) => {
      const next = new Set(prev)
      if (next.has(sc.id)) next.delete(sc.id)
      else next.add(sc.id)
      return next
    })
  }, [])

  const handleCreate = useCallback(async (data) => {
    try {
      const created = await api.post('/scenarios', {
        name: data.name,
        description: data.description,
        planVersionId: activePlan?.id,
        plant: plant.plantCode,
      })
      if (created) {
        setScenarios((prev) => [...prev, created])
      }
    } catch (err) {
      setError(err.message)
    }
  }, [activePlan, plant.plantCode])

  const handleApprove = useCallback(async ({ scenarioId, rationale }) => {
    try {
      await api.post(`/scenarios/${scenarioId}/promote`, { rationale })
      setScenarios((prev) =>
        prev.map((s) =>
          s.id === scenarioId ? { ...s, status: 'PROMOTED' } : s,
        ),
      )
    } catch (err) {
      setError(err.message)
    }
  }, [])

  const sortedScenarios = [...scenarios].sort((a, b) => {
    const pa = pinnedIds.has(a.id) ? 0 : 1
    const pb = pinnedIds.has(b.id) ? 0 : 1
    if (pa !== pb) return pa - pb
    return new Date(b.createdAt) - new Date(a.createdAt)
  })

  if (loading && plans.length === 0) {
    return <LoadingState message="Loading plans and scenarios…" />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <ForkRight color="primary" />
        <Typography variant="h3">Scenarios & Plans</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create what-if scenarios from the approved plan, compare outcomes, and promote the best option.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <PlanSelector
          plans={plans}
          activePlan={activePlan}
          scenarios={scenarios}
          activeScenario={activeScenario}
          onSelectPlan={handleSelectPlan}
          onSelectScenario={handleSelectScenario}
        />

        {compareSelection.length > 0 && (
          <Tooltip title={compareSelection.length < 2 ? 'Select 2 scenarios to compare' : 'Compare selected'}>
            <span>
              <Button
                variant="outlined"
                startIcon={<Compare />}
                disabled={compareSelection.length < 2}
                onClick={handleStartCompare}
              >
                Compare ({compareSelection.length}/2)
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {comparing && compareSelection.length === 2 && (
        <Box sx={{ mb: 3 }}>
          <CompareView
            scenarioA={compareSelection[0]}
            scenarioB={compareSelection[1]}
            onClose={handleCloseCompare}
          />
        </Box>
      )}

      <Grid container spacing={2}>
        {sortedScenarios.map((sc) => (
          <Grid key={sc.id} size={{ xs: 12, sm: 6, lg: 4 }}>
            <ScenarioCard
              scenario={sc}
              pinned={pinnedIds.has(sc.id)}
              compareSelected={compareSelection.some((s) => s.id === sc.id)}
              onOpen={(s) => { handleSelectScenario(s) }}
              onCompare={handleToggleCompare}
              onBranch={(s) => {
                setCreateOpen(true)
              }}
              onArchive={() => {}}
              onPin={handlePin}
            />
          </Grid>
        ))}
      </Grid>

      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setCreateOpen(true)}
      >
        <Add />
      </Fab>

      <CreateScenarioDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        approvedPlan={activePlan}
        existingScenarios={scenarios}
      />

      <ApprovalDialog
        open={approvalOpen}
        onClose={() => { setApprovalOpen(false); setApprovalTarget(null) }}
        onApprove={handleApprove}
        scenario={approvalTarget}
        currentApprovedPlan={activePlan}
      />
    </Box>
  )
}
